import type { FC } from 'react'
import { useRef } from 'react'
import * as Yup from 'yup'
import type { TFunction } from 'i18next'
import { useField } from 'formik'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  ListItem,
  ListItemText,
  List,
  StackColumn,
  Card,
  Box,
  useIsMobileVariant,
  IconButton,
} from '@dhl-ui/components'

import { AddressInput, useCreateAddressValidationSchema, initialAddress, type Address } from '@dhl/input-address'
import {
  CompanyNameInput,
  EmailInput,
  FirstNameInput,
  LastNameInput,
  MobileNumberInput,
  SwitchInput,
  TextFieldInput,
  VatNumberInput,
  createCompanyNameValidationSchema,
  createVatNumberValidationSchema,
} from '@dhl/inputs'
import type { ProfileAddress, ProfileAddressDetails } from '@dhl/receiver-profile-api'

import { Grid } from '@dhl/theme'
import { DeleteIcon } from '@dhl-ui/icons'
import { AddressFormDeliveryPreference, type Params } from './AddressFormDeliveryPreference.js'
import { getConfig, getTopLevelDomain, isFeatureEnabled } from '~/config/index.js'
import { AddressPreviewComponent } from '~/components/AddressPreviewComponent.js'

export type FormValues = Omit<ProfileAddress, 'addressId'>

const {
  features: {
    profile: {
      address: { mobileNumberRequired: mobileNumberRequiredForAddress },
    },
  },
} = getConfig()

const initialAddressDetails = {
  firstName: '',
  lastName: '',
  email: '',
  mobileNumber: '',
  companyName: '',
  vatNumber: '',
}

export const initialValues: FormValues = {
  name: '',
  isDefaultShippingAddress: false,
  address: {
    ...initialAddress,
    countryCode: getTopLevelDomain().toLocaleUpperCase(),
  },
  addressDetails: initialAddressDetails,
}

const addressDetailsValidationSchema = (
  t: TFunction,
  isDefaultShippingAddress: boolean
): Yup.Schema<ProfileAddressDetails> => {
  const optionalMobileNumber = Yup.string().default('')
  const requireMobileNumber = Yup.string()
    .default('')
    .required(t('inputs.mobileNumberInput.mobileNumberIsRequiredErrorMessage'))
  const mobileNumberSchema = mobileNumberRequiredForAddress(isDefaultShippingAddress)
    ? requireMobileNumber
    : optionalMobileNumber

  return Yup.object().shape({
    firstName: Yup.string().default(''),
    lastName: Yup.string().default(''),
    email: Yup.string().email(t('inputs.emailInput.emailIsInvalidErrorMessage')).default(''),
    mobileNumber: mobileNumberSchema,
    vatNumber: createVatNumberValidationSchema(t),
    companyName: createCompanyNameValidationSchema(t),
  })
}

export const useCreateProfileAddressValidationSchema = (): Yup.ObjectSchema<
  {
    name: string
    address: Address
    isDefaultShippingAddress: NonNullable<boolean | undefined>
    addressDetails: object
  },
  Yup.AnyObject,
  {
    name: undefined
    address: unknown
    isDefaultShippingAddress: undefined
    addressDetails: object
  }
> => {
  const addressValidationSchema = useCreateAddressValidationSchema('parcel', getTopLevelDomain().toUpperCase())
  const { t } = useTranslation()

  return Yup.object().shape({
    name: Yup.string().required(t('portal.profile.addressForm.nameIsRequiredErrorMessage')),
    address: addressValidationSchema,
    isDefaultShippingAddress: Yup.bool().required(),
    addressDetails: Yup.object().when('isDefaultShippingAddress', {
      is: true,
      then: () => addressDetailsValidationSchema(t, true),
      otherwise: () => addressDetailsValidationSchema(t, false),
    }),
  })
}

export const getAddressId = (values: FormValues, addresses: ProfileAddress[]): string | undefined =>
  addresses.find(
    (profileAddress) =>
      profileAddress.name === values.name &&
      profileAddress.address.postalCode.replace(/\s/g, '') ===
        (values.address.postalCode ? values.address.postalCode.replace(/\s/g, '') : values.address.postalCode) &&
      profileAddress.address.houseNumber === values.address.houseNumber &&
      profileAddress.address.houseNumberSuffix?.toLowerCase() === values.address.houseNumberSuffix?.toLowerCase()
  )?.addressId

type Props = {
  inputFields?: {
    showVatNumber?: boolean
    showCompanyName?: boolean
  }
  isAddressMutated?: boolean
  showDeliveryPreferences?: boolean
  onDelete?: () => void
}

export const AddressForm: FC<Props> = ({
  inputFields,
  isAddressMutated,
  onDelete,
  showDeliveryPreferences = isFeatureEnabled('profile.deliveryPreferences'),
}) => {
  const { t } = useTranslation()
  const { addressId } = useParams<Params>()
  const isMobileVariant = useIsMobileVariant()
  const [defaultShippingAddress] = useField<boolean>('isDefaultShippingAddress')

  const { showCompanyName = true, showVatNumber = true } = inputFields ?? {}
  const defaultAddressSwitchRef = useRef<HTMLButtonElement | null>(null)

  const handleDefaultShippingAddressChange = () =>
    requestAnimationFrame(() => defaultAddressSwitchRef?.current?.scrollIntoView())

  return (
    <StackColumn gap={2}>
      <Card>
        <Card.Header
          endNode={
            !isMobileVariant && onDelete ? (
              <IconButton size="xs" aria-label="Delete" data-testid="form-subpage-delete-button" onClick={onDelete}>
                <DeleteIcon color="error" />
              </IconButton>
            ) : undefined
          }
          title={t('portal.common.fillInYourDetails')}
        />
        <Card.Content>
          <Grid container direction="column" spacing={2}>
            <Grid>
              <TextFieldInput
                name="name"
                type="text"
                helperText={t('portal.profile.addressForm.addressNameHelperText')}
                label={t('portal.profile.addressForm.addressNameLabel')}
                required
                fullWidth
                autoFocus={!isMobileVariant}
              />
            </Grid>
            {defaultShippingAddress.value && (
              <>
                <Grid>
                  <FirstNameInput name="addressDetails.firstName" fullWidth />
                </Grid>
                <Grid>
                  <LastNameInput name="addressDetails.lastName" fullWidth />
                </Grid>
              </>
            )}

            <Grid>
              <AddressInput
                isAutoCompleteEnabled
                carrier="parcel"
                country={getTopLevelDomain().toUpperCase()}
                name="address"
                PreviewComponent={AddressPreviewComponent}
              />
            </Grid>

            {defaultShippingAddress.value && (
              <>
                <Grid>
                  <EmailInput name="addressDetails.email" data-test="address-email-input" fullWidth />
                </Grid>
                <Grid>
                  <MobileNumberInput
                    name="addressDetails.mobileNumber"
                    data-test="address-mobile-input"
                    fullWidth
                    required={mobileNumberRequiredForAddress(defaultShippingAddress.value)}
                  />
                </Grid>
                {showCompanyName && (
                  <Grid>
                    <CompanyNameInput name="addressDetails.companyName" fullWidth />
                  </Grid>
                )}
                {showVatNumber && (
                  <Grid>
                    <VatNumberInput name="addressDetails.vatNumber" fullWidth />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Card.Content>
      </Card>

      <Card>
        <Card.Header title={t('portal.profile.addressForm.additionalOptions')} />

        <Card.Content noPaddings>
          <List>
            <ListItem
              hasDivider={showDeliveryPreferences && !!addressId}
              alignItems="flex-start"
              data-test="default-switch"
            >
              <ListItemText
                emphasis
                primary={t('portal.profile.addressForm.makeDefaultAddressText')}
                secondary={t('portal.profile.addressForm.makeDefaultAddressExplanationText')}
              />
              <Box sx={{ mr: -2, mt: -1 }}>
                <SwitchInput
                  id="make-default"
                  name="isDefaultShippingAddress"
                  color="success"
                  ref={defaultAddressSwitchRef}
                  onChangeCapture={handleDefaultShippingAddressChange}
                />
              </Box>
            </ListItem>

            {showDeliveryPreferences && <AddressFormDeliveryPreference isAddressMutated={isAddressMutated} />}
          </List>
        </Card.Content>
      </Card>
    </StackColumn>
  )
}
